import * as React from "react";

export type ThemeChoice = "system" | "light" | "dark";
const KEY = "deedbeacon-theme";

function read(): ThemeChoice {
  try {
    const value = localStorage.getItem(KEY);
    return value === "light" || value === "dark" ? value : "system";
  } catch {
    return "system";
  }
}

function resolve(choice: ThemeChoice): "light" | "dark" {
  if (choice !== "system") return choice;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * KORI's dark palette is scoped to [data-theme="dark"]; this maps the viewer's choice, or the
 * operating system's setting, onto that attribute on <html> and follows OS changes live.
 */
export function useTheme() {
  const [choice, setChoice] = React.useState<ThemeChoice>(read);
  const [resolved, setResolved] = React.useState<"light" | "dark">(() => resolve(read()));

  React.useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const next = resolve(choice);
      document.documentElement.setAttribute("data-theme", next);
      setResolved(next);
    };
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [choice]);

  const update = React.useCallback((next: ThemeChoice) => {
    try {
      if (next === "system") localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, next);
    } catch {
      // Storage can be blocked; the choice still holds for this visit.
    }
    setChoice(next);
  }, []);

  return { choice, resolved, setChoice: update };
}

export const ThemeContext = React.createContext<ReturnType<typeof useTheme> | null>(null);

export function useThemeContext() {
  const value = React.useContext(ThemeContext);
  if (!value) throw new Error("useThemeContext needs ThemeContext");
  return value;
}
